const fallbackError = 'We couldn’t unlock this page. Check the password and try again.';
const verificationError = 'We couldn’t verify your submission. Please try again.';
// Mirrors `minimumSubmitTime` in config/snaptcha.php.
const snaptchaMinimumSubmitTime = 3000;

export default function protectedPageFormComponent() {
    return {
        isSubmitting: false,
        snaptchaReady: false,
        snaptchaLoadedAt: null,
        error: null,

        async init() {
            this.error = this.$el.dataset.initialError || null;

            try {
                await this.refreshSnaptchaField(this.$el);
            } catch (error) {
                console.error('Unable to load spam protection:', error);
                this.error = verificationError;
            }
        },

        async handleSubmit(event) {
            const form = event.target;

            if (!this.snaptchaReady) {
                this.error = verificationError;
                return;
            }

            this.isSubmitting = true;
            this.error = null;
            let unlocked = false;

            try {
                // Match Snaptcha's configured minimum submission time without
                // dropping an immediate click or password-manager submission.
                const snaptchaAge = Date.now() - this.snaptchaLoadedAt;
                const remainingDelay = snaptchaMinimumSubmitTime - snaptchaAge;
                if (remainingDelay > 0) {
                    await new Promise((resolve) => setTimeout(resolve, remainingDelay));
                }

                this.snaptchaReady = false;

                const response = await fetch(form.action, {
                    method: 'POST',
                    headers: {
                        'Accept': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                    body: new FormData(form),
                    credentials: 'same-origin',
                });

                const contentType = response.headers.get('content-type') || '';
                if (!contentType.includes('application/json')) {
                    throw new Error(verificationError);
                }

                const result = await response.json();

                if (!response.ok || !result.success) {
                    this.error = result.error || result.message || fallbackError;
                    return;
                }

                unlocked = true;
                window.location.assign(result.redirect);
            } catch (error) {
                console.error('Protected page unlock failed:', error);
                this.error = error.message || fallbackError;
            } finally {
                // Snaptcha keys are single-use, so a failed password attempt
                // needs a fresh key before the form can be submitted again.
                if (!unlocked) {
                    try {
                        await this.refreshSnaptchaField(form);
                    } catch (error) {
                        console.error('Unable to refresh spam protection:', error);
                        this.error = verificationError;
                    }
                }

                this.isSubmitting = false;
            }
        },

        /**
         * Fetch a unique Snaptcha field for the next unlock attempt.
         */
        async refreshSnaptchaField(form) {
            const container = form?.querySelector('[data-snaptcha-field]');
            if (!container) {
                throw new Error('Snaptcha field container is missing.');
            }

            this.snaptchaReady = false;

            const response = await fetch('/actions/snaptcha/field/get-field', {
                headers: {
                    'Accept': 'application/json',
                },
                cache: 'no-store',
                credentials: 'same-origin',
            });

            if (!response.ok) {
                throw new Error(`Spam protection failed to load (${response.status})`);
            }

            const field = await response.json();
            if (!field.name || !field.value) {
                throw new Error('Spam protection returned an invalid field.');
            }

            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = field.name;
            input.value = field.value;
            input.autocomplete = 'off';
            container.replaceChildren(input);
            this.snaptchaLoadedAt = Date.now();
            this.snaptchaReady = true;
        },
    };
}
